/**
 * useTranscriptMessages (KAN-654): feed of the transcript modal.
 *
 * A client may have several estimates-dialogs (different our Telegram
 * accounts, the old userbot, manual sends). With a known client the feed is
 * all his messages in one list (oldest first); without a client it is the
 * messages of the single estimate, as before.
 */
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { dashboardApi } from '@/services/api/dashboard';
import type { EstimateMessage } from '@/types/estimate';
import { MANUAL_CHAT_POLL_MS } from './useManualChat';

/**
 * Query key of the visible feed. The same key goes to useManualChat, so a
 * manual send refreshes exactly the feed the operator looks at.
 */
export function transcriptQueryKey(
  clientId: string | null,
  estimateId: string | null
): readonly unknown[] {
  return clientId
    ? ['client-messages', clientId]
    : ['contact-messages', estimateId];
}

interface UseTranscriptMessagesOptions {
  clientId: string | null;
  estimateId: string | null;
  enabled: boolean;
  /** Refetch the feed every MANUAL_CHAT_POLL_MS (manual mode is on). */
  poll?: boolean;
}

export function useTranscriptMessages({
  clientId,
  estimateId,
  enabled,
  poll = false,
}: UseTranscriptMessagesOptions): UseQueryResult<EstimateMessage[]> {
  const sourceId = clientId || estimateId;
  return useQuery<EstimateMessage[]>({
    queryKey: transcriptQueryKey(clientId, estimateId),
    queryFn: () =>
      clientId
        ? dashboardApi.getClientMessages(clientId)
        : dashboardApi.getContactMessages(estimateId as string),
    enabled: enabled && !!sourceId,
    refetchInterval: enabled && poll ? MANUAL_CHAT_POLL_MS : false,
  });
}
